import { getRuntimeConfig } from "@/config/runtime";
import type { ClientEvent } from "@/lib/tracker";
import type {
  ClassReportResponse,
  DistrictReportResponse,
  PortalHomeResponse,
  PortalNavigationResponse,
  PortalPopulationReferenceResponse,
  PortalSessionResponse,
  SchoolReportResponse,
  StudentReportResponse,
} from "@/contracts/portal";
import { createHttpPortalAdapter } from "@/api/adapters/http";
import { createStaticJsonPortalAdapter } from "@/api/adapters/static-json";

export interface PortalAdapter {
  getSession(): Promise<PortalSessionResponse>;
  getNavigation(): Promise<PortalNavigationResponse>;
  getPortalHome(): Promise<PortalHomeResponse>;
  getPopulationReference?(): Promise<PortalPopulationReferenceResponse | null>;
  postEvents(events: ClientEvent[], init?: RequestInit): Promise<void>;
  getDistrictReport(districtId: string): Promise<DistrictReportResponse | null>;
  getSchoolReport(districtId: string, schoolId: string): Promise<SchoolReportResponse | null>;
  getClassReport(districtId: string, schoolId: string, classId: string): Promise<ClassReportResponse | null>;
  getStudentReport(districtId: string, schoolId: string, classId: string, studentId: string): Promise<StudentReportResponse | null>;
}

let cachedAdapter: PortalAdapter | null = null;

/* Demo data (the mock adapter and its ~2 MB generated distributions) is a
   demo-mode concern only. The adapter module sits behind a dynamic import
   guarded by the BUILD-TIME mode constant below: Vite statically replaces
   import.meta.env.VITE_PORTAL_MODE, so in janison/static builds the branch
   is dead code, Rollup tree-shakes the import, and the mock chunk is not
   emitted into dist at all. Demo builds fetch it as a separate lazy chunk
   on first adapter use. The guard mirrors getRuntimeConfig()'s mode
   resolution: anything other than "janison"/"static" (including an unset
   env) is demo. The proxy keeps the synchronous getPortalAdapter()
   interface — every adapter method is already async. */
const BUILD_INCLUDES_MOCK_ADAPTER =
  import.meta.env.VITE_PORTAL_MODE !== "janison" && import.meta.env.VITE_PORTAL_MODE !== "static";

function loadMockAdapter(): Promise<PortalAdapter> {
  if (!BUILD_INCLUDES_MOCK_ADAPTER) {
    return Promise.reject(new Error("The mock adapter is not included in this build"));
  }
  return import("@/api/adapters/mock").then((module) => module.createMockPortalAdapter());
}

function createLazyMockPortalAdapter(): PortalAdapter {
  let adapterPromise: Promise<PortalAdapter> | null = null;
  const adapter = () =>
    (adapterPromise ??= loadMockAdapter()
      .catch((error: unknown) => {
        /* Do not cache a failed chunk load — a retry (e.g. the session
           gate's Try again) must be able to re-fetch it. */
        adapterPromise = null;
        throw error;
      }));

  return {
    getSession: () => adapter().then((a) => a.getSession()),
    getNavigation: () => adapter().then((a) => a.getNavigation()),
    getPortalHome: () => adapter().then((a) => a.getPortalHome()),
    getPopulationReference: () => adapter().then((a) => a.getPopulationReference?.() ?? null),
    postEvents: (events, init) => adapter().then((a) => a.postEvents(events, init)),
    getDistrictReport: (districtId) => adapter().then((a) => a.getDistrictReport(districtId)),
    getSchoolReport: (districtId, schoolId) => adapter().then((a) => a.getSchoolReport(districtId, schoolId)),
    getClassReport: (districtId, schoolId, classId) =>
      adapter().then((a) => a.getClassReport(districtId, schoolId, classId)),
    getStudentReport: (districtId, schoolId, classId, studentId) =>
      adapter().then((a) => a.getStudentReport(districtId, schoolId, classId, studentId)),
  };
}

export function getPortalAdapter(): PortalAdapter {
  if (cachedAdapter) return cachedAdapter;

  const runtime = getRuntimeConfig();
  cachedAdapter = runtime.usesMockAdapter
    ? createLazyMockPortalAdapter()
    : runtime.mode === "static"
      ? createStaticJsonPortalAdapter(runtime.apiBaseUrl)
      : createHttpPortalAdapter(runtime.apiBaseUrl);

  return cachedAdapter;
}

export function resetPortalAdapterForTests(): void {
  cachedAdapter = null;
}
